<template>
  <div class="cart-page">
    <!-- Fixed Header -->
    <header class="fixed-header">
      <div class="header-container">
        <a class="brand" href="#">
          <i class="fa fa-shopping-cart"></i>
          <span>購物車</span>
        </a>
        <button
          v-if="displayViews && displayViews.length > 0"
          class="clear-cart-btn"
          @click="clearShoppingCart()"
        >
          <i class="fa fa-trash-o"></i>
          <span class="btn-text">清空</span>
        </button>
      </div>
    </header>

    <div class="content-wrapper">
      <!-- Empty State -->
      <div
        v-if="!displayViews || displayViews.length === 0"
        class="empty-state"
      >
        <div class="empty-icon">
          <i class="fa fa-shopping-basket"></i>
        </div>
        <h2 class="empty-title">購物車是空的</h2>
        <p class="empty-subtitle">看來您還沒加入任何商品，快去選購吧！</p>
        <button class="primary-btn" @click="goToAlibabaOrder()">
          <i class="fa fa-search"></i>
          <span>前往選購</span>
        </button>
      </div>

      <!-- Cart Content -->
      <div v-else class="cart-content">
        <!-- Cart Header -->
        <div class="cart-header">
          <h2 class="cart-title">
            購物清單
            <span class="item-count">{{ displayViews.length }}</span>
          </h2>
        </div>

        <!-- Alert Tip -->
        <div class="tip-alert">
          <i class="fa fa-info-circle"></i>
          <span>若有同網址下不同款式之商品，可點選「繼續選購」完成下單喔</span>
        </div>

        <!-- Product List -->
        <div class="product-list">
          <div
            v-for="(item, index) in displayViews"
            :key="index"
            class="product-card"
          >
            <!-- Product Image -->
            <div class="product-image-wrapper">
              <img
                :src="item.color_image_selected || item.main_image_url"
                class="product-image"
                alt="Product Image"
                referrerpolicy="no-referrer"
              />
            </div>

            <!-- Product Info -->
            <div class="product-info">
              <div class="product-header">
                <h3 class="product-name">{{ item.prod_name }}</h3>
                <button
                  class="delete-btn"
                  @click="removeProd(index)"
                  title="刪除"
                >
                  <i class="fa fa-times"></i>
                </button>
              </div>

              <!-- Color Badge -->
              <div v-if="item.color_name_selected" class="color-badge">
                <span class="badge-label">顏色:</span>
                <span class="badge-value">{{ item.color_name_selected }}</span>
              </div>

              <!-- Custom Order Price Notice -->
              <div
                v-if="item.is_custom_order"
                class="custom-order-notice"
              >
                <div class="custom-order-header">
                  <i class="fa fa-pencil"></i>
                  <span>定制商品</span>
                </div>
                <div class="custom-order-price">
                  單價: ¥{{ getCustomOrderPrice(item) }}
                </div>
              </div>

              <!-- Tier Pricing Notice -->
              <div
                v-else-if="item.amount_info && item.amount_info.length > 0"
                class="tier-pricing-notice"
              >
                <div class="tier-header">
                  <i class="fa fa-tag"></i>
                  <span>階梯定價</span>
                </div>
                <div class="tier-list">
                  <span
                    v-for="(tier, tIndex) in getSortedTiers(item.amount_info)"
                    :key="tIndex"
                    class="tier-item"
                    :class="{
                      'tier-active':
                        getItemTotalQuantity(item) >=
                        parseInt(tier.amount_count),
                    }"
                  >
                    ≥{{ tier.amount_count }}件 ¥{{ tier.amount_price }}
                  </span>
                </div>
              </div>

              <!-- SKU List -->
              <div class="sku-list">
                <div
                  v-for="(sku, sIndex) in item.sku_info"
                  :key="sIndex"
                  class="sku-item"
                >
                  <template v-if="sku && sku.amount > 0">
                    <div class="sku-name">
                      <span
                        v-for="(part, pIndex) in parseSkuName(sku.sku_name)"
                        :key="pIndex"
                        :class="pIndex === 0 ? 'sku-primary' : 'sku-secondary'"
                        >{{ part }}</span
                      >
                    </div>
                    <div class="sku-right">
                      <div
                        v-if="item.is_custom_order"
                        class="sku-price"
                      >
                        ¥{{ sku.sku_price }}
                      </div>
                      <div class="sku-quantity">x {{ sku.amount }}</div>
                    </div>
                  </template>
                </div>
              </div>

              <!-- Remarks -->
              <div class="product-remarks">
                <i class="fa fa-pencil"></i>
                <span>備註: {{ item.prod_detail || '無' }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Order Summary (Desktop Sidebar) -->
        <aside class="order-summary-sidebar">
          <div class="summary-card">
            <h3 class="summary-title">訂單摘要</h3>
            <div class="summary-row">
              <span class="summary-label">商品總數</span>
              <span class="summary-value">{{ totalItemsCount }} 件</span>
            </div>
            <div class="summary-divider"></div>
            <div class="summary-total-row">
              <span class="total-label">預估總金額</span>
              <span class="total-price">¥{{ estimatedTotalPrice }}</span>
            </div>
            <button class="checkout-btn" @click="buy()">
              <i class="fa fa-check-circle"></i>
              <span>繼續完成委託單</span>
            </button>
            <button class="continue-btn" @click="goToAlibabaOrder()">
              <i class="fa fa-arrow-left"></i>
              <span>繼續選購</span>
            </button>
            <div class="trust-badge">
              <i class="fa fa-shield"></i>
              <span>安全有保障的代購服務</span>
            </div>
          </div>
        </aside>
      </div>
    </div>

    <!-- Fixed Bottom Actions (Mobile) -->
    <div v-if="displayViews && displayViews.length > 0" class="fixed-actions">
      <div class="fixed-actions-left">
        <div class="fixed-total-label">預估總金額</div>
        <div class="fixed-total-price">¥{{ estimatedTotalPrice }}</div>
      </div>
      <button class="fixed-checkout-btn" @click="buy()">繼續完成委託單</button>
    </div>

    <loading
      loader="bars"
      :active.sync="isLoading"
      :can-cancel="true"
      :is-full-page="true"
    ></loading>
    <v-dialog />
  </div>
</template>

<style>
/* CSS Variables */
:root {
  --cart-primary: #1a73e8;
  --cart-primary-dark: #1557b0;
  --cart-primary-light: #e8f0fe;
  --cart-success: #34a853;
  --cart-success-light: #e6f4ea;
  --cart-warning: #fbbc04;
  --cart-warning-light: #fef7e0;
  --cart-danger: #ea4335;
  --cart-gray-50: #f8f9fa;
  --cart-gray-100: #f1f3f4;
  --cart-gray-200: #e8eaed;
  --cart-gray-300: #dadce0;
  --cart-gray-600: #5f6368;
  --cart-gray-900: #202124;
}
</style>

<style scoped>
/* Base Layout */
.cart-page {
  min-height: 100vh;
  background-color: var(--cart-gray-50);
  padding-top: 56px;
  padding-bottom: 80px;
}

/* Fixed Header */
.fixed-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: #ffffff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  height: 56px;
}

.header-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 16px;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.brand {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 18px;
  font-weight: 700;
  color: var(--cart-primary);
  text-decoration: none;
}

.brand i {
  font-size: 20px;
}

.clear-cart-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: transparent;
  border: 1px solid var(--cart-danger);
  border-radius: 8px;
  color: var(--cart-danger);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.clear-cart-btn:hover {
  background: var(--cart-danger);
  color: white;
}

/* .btn-text is always visible */

/* Content Wrapper */
.content-wrapper {
  max-width: 1200px;
  margin: 0 auto;
  padding: 16px;
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  text-align: center;
  padding: 24px;
}

.empty-icon {
  font-size: 64px;
  color: var(--cart-gray-300);
  margin-bottom: 24px;
}

.empty-title {
  font-size: 24px;
  font-weight: 700;
  color: var(--cart-gray-900);
  margin: 0 0 12px;
}

.empty-subtitle {
  font-size: 16px;
  color: var(--cart-gray-600);
  margin: 0 0 32px;
  line-height: 1.5;
}

.primary-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 32px;
  background: var(--cart-primary);
  color: white;
  border: none;
  border-radius: 24px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.primary-btn:hover {
  background: var(--cart-primary-dark);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(26, 115, 232, 0.3);
}

/* Cart Content */
.cart-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.cart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.cart-title {
  font-size: 20px;
  font-weight: 700;
  color: var(--cart-gray-900);
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.item-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 24px;
  padding: 0 8px;
  background: var(--cart-gray-200);
  border-radius: 12px;
  font-size: 13px;
  font-weight: 600;
  color: var(--cart-gray-600);
}

/* Tip Alert */
.tip-alert {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 16px;
  background: var(--cart-warning-light);
  border-radius: 10px;
  font-size: 14px;
  color: #7c5800;
  line-height: 1.5;
}

.tip-alert i {
  flex-shrink: 0;
  margin-top: 2px;
}

/* Product List */
.product-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* Product Card */
.product-card {
  display: flex;
  gap: 12px;
  padding: 16px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;
}

.product-card:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
}

/* Product Image */
.product-image-wrapper {
  flex-shrink: 0;
  width: 80px;
  height: 80px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid var(--cart-gray-200);
}

.product-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* Product Info */
.product-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.product-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
}

.product-name {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: var(--cart-gray-900);
  line-height: 1.4;
  word-break: break-word;
  overflow-wrap: break-word;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.delete-btn {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: transparent;
  border: none;
  border-radius: 50%;
  color: var(--cart-gray-600);
  cursor: pointer;
  transition: all 0.2s ease;
}

.delete-btn:hover {
  background: var(--cart-gray-100);
  color: var(--cart-danger);
}

/* Color Badge */
.color-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: var(--cart-gray-100);
  border-radius: 6px;
  font-size: 12px;
  width: fit-content;
}

.badge-label {
  color: var(--cart-gray-600);
}

.badge-value {
  color: var(--cart-gray-900);
  font-weight: 500;
}

/* Tier Pricing Notice */
.tier-pricing-notice {
  padding: 10px 12px;
  background: var(--cart-success-light);
  border-radius: 8px;
}

.tier-header {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: var(--cart-success);
  margin-bottom: 6px;
}

.tier-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.tier-item {
  font-size: 12px;
  color: var(--cart-gray-600);
  padding: 2px 6px;
  background: white;
  border-radius: 4px;
}

.tier-active {
  background: var(--cart-success);
  color: white;
  font-weight: 600;
}

/* Custom Order Notice */
.custom-order-notice {
  padding: 10px 12px;
  background: #fff8e1;
  border: 1px solid #ffe082;
  border-radius: 8px;
}

.custom-order-header {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #e65100;
  margin-bottom: 4px;
}

.custom-order-price {
  font-size: 14px;
  font-weight: 600;
  color: var(--cart-danger);
}

/* SKU List */
.sku-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px 12px;
  background: var(--cart-gray-50);
  border-radius: 8px;
}

.sku-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
}

.sku-name {
  flex: 1;
  min-width: 0;
  font-size: 13px;
  line-height: 1.4;
  word-break: break-word;
  overflow-wrap: break-word;
}

.sku-primary {
  color: var(--cart-gray-900);
  font-weight: 500;
  margin-right: 4px;
}

.sku-secondary {
  color: var(--cart-gray-600);
  margin-right: 4px;
}

.sku-right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.sku-price {
  font-size: 13px;
  font-weight: 600;
  color: var(--cart-danger);
}

.sku-quantity {
  flex-shrink: 0;
  font-size: 13px;
  font-weight: 600;
  color: var(--cart-primary);
}

/* Product Remarks */
.product-remarks {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  font-size: 12px;
  color: var(--cart-gray-600);
  line-height: 1.4;
  word-break: break-word;
}

.product-remarks i {
  flex-shrink: 0;
  margin-top: 2px;
}

/* Order Summary Sidebar (Hidden on Mobile) */
.order-summary-sidebar {
  display: none;
}

.summary-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  padding: 24px;
}

.summary-title {
  font-size: 18px;
  font-weight: 700;
  color: var(--cart-gray-900);
  margin: 0 0 20px;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.summary-label {
  font-size: 14px;
  color: var(--cart-gray-600);
}

.summary-value {
  font-size: 14px;
  font-weight: 600;
  color: var(--cart-gray-900);
}

.summary-divider {
  height: 1px;
  background: var(--cart-gray-200);
  margin: 16px 0;
}

.summary-total-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.total-label {
  font-size: 16px;
  font-weight: 600;
  color: var(--cart-gray-900);
}

.total-price {
  font-size: 24px;
  font-weight: 700;
  color: var(--cart-danger);
}

.checkout-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 14px 20px;
  background: var(--cart-danger);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 12px;
}

.checkout-btn:hover {
  background: #c62828;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(234, 67, 53, 0.3);
}

.continue-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 12px 20px;
  background: transparent;
  color: var(--cart-gray-600);
  border: 1px solid var(--cart-gray-300);
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.continue-btn:hover {
  background: var(--cart-gray-50);
  border-color: var(--cart-gray-600);
  color: var(--cart-gray-900);
}

.trust-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-top: 16px;
  font-size: 12px;
  color: var(--cart-gray-600);
}

.trust-badge i {
  color: var(--cart-success);
}

/* Fixed Bottom Actions (Mobile) */
.fixed-actions {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 16px;
  background: white;
  box-shadow: 0 -2px 12px rgba(0, 0, 0, 0.1);
}

.fixed-actions-left {
  display: flex;
  flex-direction: column;
}

.fixed-total-label {
  font-size: 12px;
  color: var(--cart-gray-600);
}

.fixed-total-price {
  font-size: 20px;
  font-weight: 700;
  color: var(--cart-danger);
}

.fixed-checkout-btn {
  flex-shrink: 0;
  padding: 12px 24px;
  background: var(--cart-danger);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.fixed-checkout-btn:active {
  transform: scale(0.98);
}

/* ==================== TABLET BREAKPOINT ==================== */
@media (min-width: 600px) {
  .product-image-wrapper {
    width: 100px;
    height: 100px;
  }

  .product-name {
    font-size: 16px;
    -webkit-line-clamp: 3;
    line-clamp: 3;
  }

  .product-card {
    padding: 20px;
    gap: 16px;
  }
}

/* ==================== DESKTOP BREAKPOINT ==================== */
@media (min-width: 992px) {
  .cart-page {
    padding-bottom: 24px;
  }

  .cart-content {
    display: grid;
    grid-template-columns: 1fr 360px;
    gap: 24px;
    align-items: start;
  }

  .cart-header {
    grid-column: 1 / 2;
  }

  .tip-alert {
    grid-column: 1 / 2;
  }

  .product-list {
    grid-column: 1 / 2;
  }

  .order-summary-sidebar {
    display: block;
    grid-column: 2 / 3;
    grid-row: 1 / 4;
    position: sticky;
    top: 80px;
  }

  .product-image-wrapper {
    width: 120px;
    height: 120px;
  }

  .product-card:hover {
    transform: translateY(-2px);
  }

  /* Hide mobile fixed actions on desktop */
  .fixed-actions {
    display: none;
  }
}

/* ==================== LARGE DESKTOP ==================== */
@media (min-width: 1200px) {
  .cart-content {
    grid-template-columns: 1fr 400px;
  }
}
</style>

<script>
import VueLoading from 'vue-loading-overlay'
import 'vue-loading-overlay/dist/vue-loading.css'
import { isMobile } from 'mobile-device-detect'

export default {
  name: 'cart',
  components: {
    loading: VueLoading,
  },
  data: function () {
    return {
      apiUrl: process.env.VUE_APP_API_URL,
      displayViews: null,
      isLoading: true,
      amounts: [],
      checkMobile: isMobile ? '1' : '0',
      buyBaseLine: 1,
    }
  },
  computed: {
    totalItemsCount() {
      if (!this.displayViews) return 0
      let count = 0
      this.displayViews.forEach(item => {
        if (item.sku_info) {
          item.sku_info.forEach(sku => {
            count += parseInt(sku.amount || 0)
          })
        }
      })
      return count
    },
    estimatedTotalPrice() {
      const vm = this
      if (!vm.displayViews) return 0
      let total = 0

      vm.displayViews.forEach(item => {
        if (item.sku_info) {
          // 計算每個商品的總購買數量
          let itemTotalQuantity = 0
          item.sku_info.forEach(sku => {
            itemTotalQuantity += parseInt(sku.amount || 0)
          })

          // 根據總數量計算階梯價格
          const tierPrice = vm.calculateTierPrice(
            itemTotalQuantity,
            item.amount_info
          )

          // 計算該商品的總價
          item.sku_info.forEach(sku => {
            if (sku.amount > 0 && sku.sku_price) {
              // 如果有階梯定價，使用階梯價格；否則使用 SKU 價格
              const price =
                tierPrice !== null ? tierPrice : parseFloat(sku.sku_price)
              total += parseInt(sku.amount) * price
            }
          })
        }
      })
      return total.toFixed(2)
    },
  },
  methods: {
    // 根據購買數量計算階梯價格
    calculateTierPrice: function (totalQuantity, amountInfo) {
      // 如果沒有階梯定價資訊，返回 null
      if (!amountInfo || amountInfo.length === 0) {
        return null
      }

      // 將階梯定價按數量從小到大排序
      const sortedTiers = [...amountInfo].sort(
        (a, b) => parseInt(a.amount_count) - parseInt(b.amount_count)
      )

      // 找出符合購買數量的最大階梯
      let applicableTier = sortedTiers[0] // 預設使用第一階（最低量）

      for (let tier of sortedTiers) {
        const tierCount = parseInt(tier.amount_count)
        if (totalQuantity >= tierCount) {
          applicableTier = tier
        } else {
          break
        }
      }

      return parseFloat(applicableTier.amount_price)
    },
    // 計算單個商品的總購買數量
    getItemTotalQuantity: function (item) {
      if (!item || !item.sku_info) return 0
      let total = 0
      item.sku_info.forEach(sku => {
        total += parseInt(sku.amount || 0)
      })
      return total
    },
    // 排序階梯定價（從小到大）
    getSortedTiers: function (amountInfo) {
      if (!amountInfo || amountInfo.length === 0) return []
      return [...amountInfo].sort(
        (a, b) => parseInt(a.amount_count) - parseInt(b.amount_count)
      )
    },
    getCustomOrderPrice(item) {
      if (!item || !item.sku_info || item.sku_info.length === 0) return '0.00'
      const sku = item.sku_info.find(s => s.amount > 0) || item.sku_info[0]
      return parseFloat(sku.sku_price || 0).toFixed(2)
    },
    parseSkuName(name) {
      if (!name) return ['']
      // Replace html entity if present just in case, though usually handled by browser
      // Then split by '>'
      const cleanName = name.replace(/&gt;/g, '>')
      const parts = cleanName.split('>')

      // If no split happened, return as single item array
      if (parts.length === 1) return parts

      // Logic:
      // Part 0 is primary (Black)
      // Subsequent parts are secondary (Gray)
      // actually the template loop handles the styling logic based on index

      // We might want to re-add the '>' separator visually if needed,
      // but the request said:
      // 黑【拉絲款】>【胚料】7*2CM：右槽【不銹鋼單片】
      // shows as:
      // 黑【拉絲款】 (Black)
      // 【胚料】7*2CM：右槽【不銹鋼單片】 (Gray)
      // implying the '>' is removed or acts as the separator.
      // Let's return the array of parts.
      return parts.map(p => p.trim())
    },
    buy: function () {
      const vm = this

      let count = 0
      vm.displayViews.forEach(function (prodInfo) {
        vm.buyBaseLine = prodInfo.buy_base_line

        if (prodInfo.sku_info.length > 0) {
          prodInfo.sku_info.forEach(element => {
            count = parseInt(count) + parseInt(element.amount)
          })
        }
      })

      if (count === 0) {
        vm.$toast.open({
          message: '請輸入數量!',
          type: 'error',
        })
      } else if (vm.$store.state.ignoreBaseLine === false) {
        // 勾選申請打樣時，跳過起批數量檢查
        vm.$router.replace('profilev1.1')
      } else {
        // 未勾選申請打樣或狀態未知時，需要檢查起批數量
        if (count < vm.buyBaseLine) {
          vm.$toast.open({
            message:
              '注意低於起批數量' +
              vm.buyBaseLine +
              '個，如需購買可至商品頁勾選「申請打樣」',
            type: 'error',
          })
        } else {
          vm.$router.replace('profilev1.1')
        }
      }
    },
    removeProd: function (index) {
      const vm = this
      vm.displayViews.splice(index, 1)
      vm.$store.prodInfos = vm.displayViews
      localStorage.setItem('prodInfos', JSON.stringify(vm.displayViews))
      vm.$forceUpdate()

      vm.$toast.open({
        message: '商品已刪除',
        type: 'info',
        duration: 2000,
      })
    },
    clearShoppingCart: function () {
      if (!confirm('確定要清空購物車嗎？')) return

      const vm = this
      vm.displayViews = []
      vm.$store.prodInfos = []
      localStorage.setItem('prodInfos', JSON.stringify([]))
      vm.$forceUpdate()

      vm.$toast.open({
        message: '購物車已清空',
        type: 'success',
        duration: 2000,
      })
    },
    checkUnChatbotSignin: function () {
      const vm = this

      try {
        if (
          vm.$route.query.userId &&
          vm.$route.query.userImage &&
          vm.$route.query.userName
        ) {
          localStorage.userId = decodeURI(vm.$route.query.userId)
          localStorage.userImage = decodeURI(vm.$route.query.userImage)
          localStorage.userName = decodeURI(vm.$route.query.userName)
          vm.userId = decodeURI(vm.$route.query.userId)
          vm.userImage = decodeURI(vm.$route.query.userImage)
          vm.userName = decodeURI(vm.$route.query.userName)
        }
      } catch (e) {
        alert(e)
      }

      if (
        localStorage.userId === undefined ||
        localStorage.userImage === undefined ||
        localStorage.userName === undefined
      ) {
        vm.$modal.show('dialog', {
          title: '🙋您尚未登入FB會員',
          text: '請先登入才能使用此服務喔⚡',
          buttons: [
            {
              title: '👉用FB登入',
              handler: () => {
                window.location = 'https://m.me/2266831083395361'
              },
            },
          ],
        })
        return true
      } else {
        return false
      }
    },
    goToAlibabaOrder: function () {
      const vm = this
      vm.$router.replace('alibabaorderv1.1')
    },
  },
  mounted() {
    const vm = this

    document.body.scrollTop = 0
    document.documentElement.scrollTop = 0

    vm.$ga.event('event', 'conversion', {
      send_to: 'AW-700538278/aKz7CJu94rIBEKa7hc4C',
      value: 1.0,
      currency: 'TWD',
    })

    vm.checkUnChatbotSignin()

    if (vm.$store.prodInfos !== undefined && vm.$store.prodInfos !== null) {
      vm.displayViews = vm.$store.prodInfos
      vm.isLoading = false
    } else if (
      JSON.parse(localStorage.getItem('prodInfos')) !== undefined &&
      JSON.parse(localStorage.getItem('prodInfos')) !== null
    ) {
      vm.displayViews = JSON.parse(localStorage.getItem('prodInfos'))
      vm.isLoading = false
    }

    if (vm.displayViews === undefined || vm.displayViews === null) {
      vm.$modal.show('dialog', {
        title: '🙋請聯絡客服',
        text: '請聯絡客服⚡ 系統異常!',
        buttons: [
          {
            title: '👉聯絡客服',
            handler: () => {
              window.location = 'https://m.me/2266831083395361'
            },
          },
        ],
      })
    }
  },
}
</script>
